import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import { ReactComponent as CopyIcon } from "../../assets/icons/copyIcon.svg";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import { useKeysContext } from "../../context/useKeysContext";
import { copyToClipboard } from "../../helper/copy";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { showGenericError, showErrorNotification } from "../../helper/Notifications";
import CustomModal from "../../Components/Generic/CustomModal";
import LanguagePicker from "../../Components/Generic/LanguagePicker";
import DomainsInput from "../../Components/Generic/DomainsInput";
import CustomInput from "../../Components/Generic/CustomInput";
import Tooltip from "../../Components/Generic/Tooltip";
import { AnimatePresence } from "framer-motion";
import BulkActionBar from "../../Components/Generic/BulkActionBar";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import EmbedDocumentationPageModal from "../../Components/EmbedPage/EmbedDocumentationPageModal";
import LanguagesTooltip from "../../Components/EmbedPage/LanguagesTooltip";
import { useLicensingContext } from "../../context/useLicensingContext";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import { getTranslationLanguagesAPICall } from "../../api/keys/keyService";
import SubscriptionCards from "../SubscriptionsPage/SubscriptionCards";
import Dropdown from "../../Components/Generic/Dropdown";
import Toggle from "../../Components/Generic/Toggle";
import { CURRENCY_OPTIONS, WEBSITE_TRANSLATION } from "../../constants/OptionsConstants";
import "rsuite/dist/rsuite.min.css";
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import PageHeader from "../../Components/Layout/PageHeader";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import MemberAccessBanner from "../../Components/Generic/MemberAccessBanner";
import { executeBulkDelete } from "../../helper/bulkOperations";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import ProductUsageWidget from "../../Components/Generic/ProductUsageWidget";
import { formatDateRangeForAPI, getDefaultMonthToDateRange, isDateRangeSelected } from "../../helper/dateFormat";

const EmbedPage = () => {
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [keyToEdit, setKeyToEdit] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [keyDomains, setKeyDomains] = useState(['']);
  const [keyLanguages, setKeyLanguages] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);
  const hasDateRange = isDateRangeSelected(dateRange);
  const [totalPages, setTotalPages] = useState(1);
  const [usageMap, setUsageMap] = useState({});
  const { embedKeys, deleteEmbedKey, getEmbedKeys, createEmbedKey, updateEmbedKey } = useKeysContext();
  const { selectedProject } = useProjectContext();
  const { subscription, getEmbedKeyUsage } = useLicensingContext();
  const { setLoading, setShowSupportModal } = useOutletContext();
  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");
  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();
  const { hasProductAccess: hasMemberEmbedAccess } = useMemberProductAccess("website_translation");
  const embedItem = subscription?.items?.find(i => i.product_slug === "website_translation");
  const hasEmbedAccess = !!embedItem || hasMemberEmbedAccess;
  const hasExistingKeys = embedKeys && embedKeys.length > 0;

  const mapKey = (key) => {
    if (key.length > 20) {
      return key.slice(0, 6) + "***************" + key.slice(-6);
    } else return key;
  };

  const formatLanguages = (key) => {
    const langs = key.allowed_languages || [];
    if (langs.length === 0) return "All";
    const totalAvailable = getOfferingLanguages().length;
    if (totalAvailable > 0 && langs.length >= totalAvailable) return "All";
    return `${langs.length} language${langs.length !== 1 ? 's' : ''}`;
  };

  const handleGetKeys = () => {
    getEmbedKeys(
      selectedProject.id,
      page,
      itemsPerPage,
      (response) => {
        setTotalPages(response.data.pagination?.total_pages || 1);
        setLoading(false);
      },
      () => {
        showGenericError();
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (!selectedProject) return;
    if (subscription === undefined) return;
    if (!hasEmbedAccess && !hasExistingKeys) return;
    handleGetKeys();
  }, [selectedProject, page, itemsPerPage, hasEmbedAccess]);

  // Fetch available translation languages
  useEffect(() => {
    getTranslationLanguagesAPICall(
      (response) => {
        setAvailableLanguages(response.data.languages || []);
      },
      (error) => {
        console.error("Failed to fetch languages:", error);
      }
    );
  }, []);

  // Fetch embed usage aggregated by key for the selected date range
  useEffect(() => {
    if (!selectedProject) return;
    if (!hasEmbedAccess && !hasExistingKeys) return;
    const payload = {
      per_page: 1000,
      sort_by: "words",
      ascending: false,
      ...(hasDateRange && formatDateRangeForAPI(dateRange[0], dateRange[1])),
    };
    getEmbedKeyUsage(
      selectedProject.id,
      payload,
      (response) => {
        // Breakdown returns one row per user+api_key combination — aggregate by api_key.id
        const items = response.data?.items || [];
        const map = {};
        items.forEach((item) => {
          const keyId = item.api_key?.id ?? item.api_key_id;
          if (keyId) {
            if (!map[keyId]) map[keyId] = { words: 0, requests: 0 };
            map[keyId].words += item.words || 0;
          }
        });
        setUsageMap(map);
      },
      (err) => {
        console.error("Embed usage error:", err);
      }
    );
  }, [selectedProject, hasEmbedAccess, hasExistingKeys, dateRange]);

  const handleOpenDeleteModal = (key) => {
    setKeyToDelete(key);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setKeyToDelete(null);
  };

  const handleDeleteKey = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      deleteEmbedKey(
        selectedProject.id,
        keyToDelete.id,
        (response) => {
          handleGetKeys();
          resolve();
        },
        (error) => {
          showGenericError();
          setLoading(false);
          reject(error);
        }
      );
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = embedKeys.map((key) => key.id);
      setSelectedKeys(allIds);
    } else {
      setSelectedKeys([]);
    }
  };

  const handleSelectOne = (e, keyId) => {
    if (e.target.checked) {
      setSelectedKeys((prev) => [...prev, keyId]);
    } else {
      setSelectedKeys((prev) => prev.filter((id) => id !== keyId));
    }
  };

  const handleBulkDeleteKeys = () => {
    const deleteSingleKey = (keyId) => {
      return new Promise((resolve, reject) => {
        deleteEmbedKey(selectedProject.id, keyId, resolve, reject);
      });
    };

    return executeBulkDelete({
      items: selectedKeys,
      deleteAction: deleteSingleKey,
      onSuccess: () => {
        handleGetKeys();
        setSelectedKeys([]);
      },
      setLoading: setLoading
    });
  };

  const getOfferingLanguages = () => {
    if (embedItem?.allowed_languages && embedItem.allowed_languages.length > 0) {
      return embedItem.allowed_languages;
    }
    return availableLanguages;
  };

  const resetEmbedModalFields = () => {
    setKeyName('');
    setKeyDomains(['']);
    setKeyLanguages([]);
  };

  const canAddMoreKeys = () => {
    const keyLimit = embedItem?.max_keys;
    if (!keyLimit) return true;
    const currentCount = embedKeys?.length || 0;
    return currentCount < keyLimit;
  };

  const handleOpenAddModal = () => {
    if (!canAddMoreKeys()) {
      const keyLimit = embedItem?.max_keys;
      showErrorNotification(`Maximum of ${keyLimit} embed key${keyLimit === 1 ? '' : 's'} allowed`);
      return;
    }
    setKeyToEdit(null);
    resetEmbedModalFields();
    setShowEmbedModal(true);
  };

  const handleOpenEditModal = (key) => {
    setKeyToEdit(key);
    setKeyName(key.name);
    setKeyDomains(key.allowed_domains && key.allowed_domains.length > 0 ? key.allowed_domains : ['']);
    setKeyLanguages(key.allowed_languages || []);
    setShowEmbedModal(true);
  };

  const handleCloseEmbedModal = () => {
    setShowEmbedModal(false);
    setKeyToEdit(null);
    resetEmbedModalFields();
  };

  const handleDomainChange = (index, value) => {
    const updatedDomains = [...keyDomains];
    updatedDomains[index] = value;
    setKeyDomains(updatedDomains);
  };

  const handleAddDomain = () => {
    const domainLimit = embedItem?.max_domains_per_key;
    if (domainLimit && keyDomains.length >= domainLimit) {
      showErrorNotification(`Maximum of ${domainLimit} domain${domainLimit === 1 ? '' : 's'} per key allowed`);
      return;
    }
    setKeyDomains([...keyDomains, '']);
  };

  const handleRemoveDomain = (index) => {
    if (keyDomains.length > 1) {
      setKeyDomains(keyDomains.filter((_, i) => i !== index));
    }
  };

  const handleCreateEmbedKey = async () => {
    setLoading(true);

    try {
      const domains = keyDomains.filter(d => d.trim().length > 0);

      if (!keyName.trim() || keyName.trim().length < 3) {
        showErrorNotification("Please provide a valid name (min 3 characters)");
        setLoading(false);
        return Promise.reject(new Error("Invalid name"));
      }

      if (!domains || domains.length === 0) {
        showErrorNotification("At least one allowed domain is required");
        setLoading(false);
        return Promise.reject(new Error("No domains provided"));
      }

      const domainLimit = embedItem?.max_domains_per_key;
      if (domainLimit && domains.length > domainLimit) {
        showErrorNotification(`Maximum of ${domainLimit} domain${domainLimit === 1 ? '' : 's'} per key allowed`);
        setLoading(false);
        return Promise.reject(new Error("Domain limit exceeded"));
      }

      await new Promise((resolve, reject) => {
        createEmbedKey(
          selectedProject.id,
          keyName,
          domains,
          keyLanguages,
          (response) => resolve(),
          (error) => reject(error)
        );
      });

      handleGetKeys();
      resetEmbedModalFields();
      return Promise.resolve();
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage) {
        showErrorNotification(errorMessage);
      } else {
        showGenericError();
      }
      setLoading(false);
      return Promise.reject(error);
    }
  };

  const handleUpdateEmbedKey = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const domains = keyDomains.filter(d => d.trim().length > 0);

      if (domains.length === 0) {
        showErrorNotification("At least one allowed domain is required");
        setLoading(false);
        reject(new Error("No domains provided"));
        return;
      }

      const domainLimit = embedItem?.max_domains_per_key;
      if (domainLimit && domains.length > domainLimit) {
        showErrorNotification(`Maximum of ${domainLimit} domain${domainLimit === 1 ? '' : 's'} per key allowed`);
        setLoading(false);
        reject(new Error("Domain limit exceeded"));
        return;
      }

      updateEmbedKey(
        selectedProject.id,
        keyToEdit.id,
        keyName,
        domains,
        keyLanguages,
        (response) => {
          handleGetKeys();
          resolve();
        },
        (error) => {
          const errorMessage = error?.response?.data?.message;
          if (errorMessage) {
            showErrorNotification(errorMessage);
          } else {
            showGenericError();
          }
          setLoading(false);
          reject(error);
        }
      );
    });
  };

  useEffect(() => {
    if (subscription?.currency) {
      setCurrency(subscription.currency);
    }
  }, [subscription]);

  // Wait for offering to load before showing any content
  if (subscription === undefined) {
    return <LoaderComponent loading={true} />;
    
  }
  

   const hasSelection = selectedKeys.length > 0;

  return (
    <Container>
      <AnimatePresence>
        {showDocumentationModal && (
          <EmbedDocumentationPageModal
            setShowDocumentationModal={setShowDocumentationModal}
          />
        )}
      </AnimatePresence>
      <CustomModal
        isOpen={showBulkDeleteModal}
        onOpenChange={() => setShowBulkDeleteModal(false)}
        iconType="warning"
        iconAlignment="center"
        title={`Delete ${selectedKeys.length} Embed key${selectedKeys.length === 1 ? '' : 's'}?`}
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: () => setShowBulkDeleteModal(false),
            variant: "secondary"
          },
          {
            text: `Delete ${selectedKeys.length} Key${selectedKeys.length === 1 ? '' : 's'}`,
            action: handleBulkDeleteKeys,
            variant: "delete",
            successMessage: `Embed key${selectedKeys.length === 1 ? '' : 's'} deleted successfully`
          }
        ]}
      >
        <ModalContentText>
          This will immediately revoke <strong>{selectedKeys.length}</strong> key{selectedKeys.length === 1 ? '' : 's'} and disable any embedded translations using {selectedKeys.length === 1 ? 'it' : 'them'}. You cannot recover {selectedKeys.length === 1 ? 'this key' : 'these keys'} once deleted.
        </ModalContentText>
      </CustomModal>

      {/* Delete Embed Key Modal */}
      <CustomModal
        isOpen={showDeleteModal}
        onOpenChange={handleCloseDeleteModal}
        iconType="warning"
        iconAlignment="center"
        title="Delete this embed key?"
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseDeleteModal,
            variant: "secondary"
          },
          {
            text: "Delete Key",
            action: handleDeleteKey,
            variant: "delete",
            successMessage: "Embed key deleted successfully"
          }
        ]}
      >
        <ModalContentText>
          This will immediately revoke the key and disable any embedded translations using it. You cannot recover this key once deleted - you'll need to generate a new one if needed.
        </ModalContentText>
      </CustomModal>

      <CustomModal
        isOpen={showEmbedModal}
        onOpenChange={handleCloseEmbedModal}
        iconType="apiKey"
        iconAlignment="left"
        headerLayout="row"
        title={keyToEdit ? "Edit Embed Key" : "Add Embed Key"}
        subtitle={keyToEdit ? "Update the name and allowed domains for this embed key." : "Create a secure key for your website translation widget and restrict its use to approved domains."}
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={keyToEdit ? 680 : 600}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseEmbedModal,
            variant: "secondary"
          },
          {
            text: keyToEdit ? "Save" : "Add",
            action: keyToEdit ? handleUpdateEmbedKey : handleCreateEmbedKey,
            variant: "primary",
            disabled: keyName.trim().length < 3 || !keyDomains.some(d => d.trim().length > 0) || keyLanguages.length === 0,
            successMessage: keyToEdit ? "Embed key updated successfully" : "Embed key created successfully"
          }
        ]}
      >
        <ModalContent>
          <CustomInput
            value={keyName}
            onChange={(e) => setKeyName(e.currentTarget.value)}
            label={<>{keyToEdit ? "Name" : "Enter a name"} <RequiredAsterisk>*</RequiredAsterisk></>}
            labelClassName="font-bold"
            placeholder="Eq. Company Website"
          />

          <Divider />

          <DomainsSection>
            <Label>Allowed Domains <RequiredAsterisk>*</RequiredAsterisk></Label>
            <DomainsInput
              domains={keyDomains}
              onChange={handleDomainChange}
              onAdd={handleAddDomain}
              onRemove={handleRemoveDomain}
            />
          </DomainsSection>

          <Divider />

          <LanguagesSection>
            <Label>Allowed Languages <RequiredAsterisk>*</RequiredAsterisk></Label>
            <LanguageDescription>
              Select all languages or a subset of languages for this domain{getOfferingLanguages().length < availableLanguages.length ? ' available in your plan' : ''}.
            </LanguageDescription>
            <LanguagePicker
              languages={getOfferingLanguages()}
              selected={keyLanguages}
              onChange={setKeyLanguages}
            />
          </LanguagesSection>
        </ModalContent>
      </CustomModal>

      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={(hasEmbedAccess || hasExistingKeys) ? WEBSITE_TRANSLATION : `${WEBSITE_TRANSLATION} Plans`}
              subtitle={(hasEmbedAccess || hasExistingKeys)
                ? "Embed real-time translation widgets on your websites with domain-specific API keys."
                : "Choose a plan to unlock website translation and start embedding multilingual widgets."}
            />
          </HeaderContent>
          <HeaderRight>
            {!(hasEmbedAccess || hasExistingKeys) && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
            {(hasEmbedAccess || hasExistingKeys) && (

              
              <ButtonsContainer>
                
                
                <SecondaryButton
                  icon={<CodeBracketsIcon />}
                  text={"See Documentation"}
                  onClick={() => setShowDocumentationModal(true)}
                  variant="brandTint"
                />
                <PrimaryButton
                  icon={<EmbedIcon />}
                  text={"Add Embed Key"}
                  onClick={handleOpenAddModal}
                />
              </ButtonsContainer>
            )}
          </HeaderRight>
        </Header>
        {(hasEmbedAccess || hasExistingKeys) && <MemberAccessBanner />}
        {(hasEmbedAccess || hasExistingKeys) && selectedProject && (
          <ProductUsageWidget
            projectId={selectedProject.id}
            productSlug="website_translation"
            dateRange={dateRange}
            calendarAction={
              <AppDateRangePicker
                value={dateRange}
                onChange={(newRange) => setDateRange(newRange)}
              />
            }
          />
        )}
        {!(hasEmbedAccess || hasExistingKeys) ? (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle
                    checked={annualPricing}
                    onCheckedChange={setAnnualPricing}
                  />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>

            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSupportModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product="website_translation"
            />
          </PlanCardsSection>
        ) : (
          <>
            <StyledTable>
           <TableHeader>
              
              <BulkActionBar 
                selectedCount={selectedKeys.length} 
                onDelete={() => setShowBulkDeleteModal(true)} 
                onClear={() => setSelectedKeys([])} 
              />
              
              <TableRow>
                <TableHead style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                  <CheckboxContainer $alwaysVisible={hasSelection}>
                    <input
                      type="checkbox"
                      style={{ cursor: "pointer", margin: 0 }}
                      checked={
                        embedKeys?.length > 0 &&
                        selectedKeys.length === embedKeys.length
                      }
                      onChange={handleSelectAll}
                    />
                  </CheckboxContainer>
                </TableHead>
                <TableHead style={{ textAlign: "left", width: "20%" }}>Name</TableHead>
                <TableHead style={{ textAlign: "center" }}>Domains</TableHead>
                <TableHead style={{ textAlign: "center" }}>Languages</TableHead>
                <TableHead style={{ textAlign: "center" }}>Value</TableHead>
                <TableHead style={{ textAlign: "center" }}>Words</TableHead>
                <TableHead style={{ width: "150px", textAlign: "center" }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
              <TableBody>
                {embedKeys && embedKeys.length > 0 ? (
                  embedKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                        <CheckboxContainer $alwaysVisible={hasSelection || selectedKeys.includes(key.id)}>
                          <input
                            type="checkbox"
                            style={{ cursor: "pointer", margin: 0 }}
                            checked={selectedKeys.includes(key.id)}
                            onChange={(e) => handleSelectOne(e, key.id)}
                          />
                        </CheckboxContainer>
                      </TableCell>
                      
                      <TableCell>
                        <KeyNameContainer>
                          <KeyIconWrapper>
                            <EmbedKeyIcon />
                          </KeyIconWrapper>
                          <KeyName>{key.name}</KeyName>
                        </KeyNameContainer>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <DomainCount>
                          {key.allowed_domains && key.allowed_domains.length > 0
                            ? `${key.allowed_domains.length} domain${key.allowed_domains.length !== 1 ? 's' : ''}`
                            : 'No domains'}
                        </DomainCount>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <LanguagesTooltip languages={key.allowed_languages}>
                          <DomainCount>{formatLanguages(key)}</DomainCount>
                        </LanguagesTooltip>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <KeyValue data-emotii-exclude>{mapKey(key.key)}</KeyValue>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <WordsValue>
                          {usageMap[key.id] != null
                            ? usageMap[key.id].words.toLocaleString()
                            : "—"}
                        </WordsValue>
                      </TableCell>
                      <TableCell style={{ width: "150px" }}>
                        <ActionButtonsContainer>
                          <Tooltip content="Edit Key" side="top">
                            <EditButton onClick={() => handleOpenEditModal(key)}>
                              <EditIcon />
                            </EditButton>
                          </Tooltip>
                          <Tooltip content="Copy the API Key" side="top">
                            <CopyButton onClick={() => copyToClipboard(key.key)}>
                              <CopyIcon />
                            </CopyButton>
                          </Tooltip>
                          <Tooltip content="Copy Embed Script" side="top">
                            <CopyButton onClick={() => copyToClipboard(`<script
                            src="https://emotii.ai/emotii.ai.embedded.js"
                            data-emotii-key="${key.key}"
                            data-emotii-default-language="English"
                          ></script>`)}>
                              <EmbedScriptIcon />
                            </CopyButton>
                          </Tooltip>
                          <DeleteButton onClick={() => handleOpenDeleteModal(key)}>
                            <BinIcon />
                          </DeleteButton>
                        </ActionButtonsContainer>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <NoKeysText>
                        No embed keys yet. Create your first embed key to add translation widgets to your websites. Each key can be restricted to specific domains for security.
                      </NoKeysText>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TablePagination
                page={page}
                totalPages={totalPages}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
                leftColSpan={3}
                centerColSpan={1}
                rightColSpan={3}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
              />
            </StyledTable>
          </>
        )}
      </PageContent>
    </Container>
  );
};

export default EmbedPage;

// Icons
const EmbedIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M6.66667 14.1667L2.5 10L6.66667 5.83334M13.3333 5.83334L17.5 10L13.3333 14.1667" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EmbedKeyIcon = (props) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect width="40" height="40" rx="20" fill="#F4EBFF"/>
    <path d="M16 15L11 20L16 25M24 15L29 20L24 25" stroke="#7F56D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EditIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M11.3333 2.00004C11.5084 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.419 1.44775 12.6666 1.44775C12.9143 1.44775 13.1598 1.49653 13.3886 1.59129C13.6173 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.38272 14.4087 2.61146C14.5035 2.8402 14.5523 3.08569 14.5523 3.33337C14.5523 3.58106 14.5035 3.82655 14.4087 4.05529C14.314 4.28403 14.1751 4.49162 14 4.66671L5.00004 13.6667L1.33337 14.6667L2.33337 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EmbedScriptIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M5 11L1.66667 8L5 5M11 5L14.3333 8L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CodeBracketsIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M6.66667 14.1667L2.5 10L6.66667 5.83334M13.3333 5.83334L17.5 10L13.3333 14.1667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px 30px 18px 30px;
  position: relative;
  height: 100%;
  background: #F9FAFB;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  color: ${lightThemeColors.darkColor};
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: left;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-height: min(60vh, 520px);
  overflow-y: auto;
  padding-right: 12px;
  padding-left: 4px;
  margin-left: -4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const ModalContentText = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

const KeyNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KeyIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 40px;
    height: 40px;
    display: block;
  }
`;

const KeyName = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const KeyValue = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: 'Courier New', monospace;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f4ebff;
    color: #7f56d9;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f4ebff;
    color: #7f56d9;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const NoKeysText = styled.div`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  width: 100%;
  padding: 40px 20px;
`;
const CheckboxContainer = styled.div`
  opacity: ${(props) => (props.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  tr:hover & {
    opacity: 1;
  }
`;
const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #D0D5DD;
  flex-shrink: 0;
`;

const Label = styled.label`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const RequiredAsterisk = styled.span`
  color: #d92d20;
`;

const DomainsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const DomainCount = styled.span`
  color: #9CA3AF;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
`;

const LanguagesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LanguageDescription = styled.p`
  font-size: 14px;
  color: ${lightThemeColors.mutedColor};
  margin: 0;
`;

const WordsValue = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
